import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Eraser,
  Euro,
  FileText,
  ListChecks,
  Loader2,
  PenLine,
  Trash2,
  User2,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import {
  companiesQueries,
  type BranchDto,
  type CompanyDto,
  type LegalRepresentativeDto,
} from "@/lib/companies/api";
import {
  contractsApi,
  contractsQueries,
  EMPTY_GUID,
  employeeDisplayName,
  toEmployeeSnapshot,
  type ContractConfigurationDto,
  type OrdioEmployeeDetailDto,
} from "@/lib/contracts/api";
import { useToastMutation } from "@/lib/mutations";
import { ApiError } from "@/api/fetch";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SignaturePad, type SignaturePadHandle } from "@/components/contracts/SignaturePad";
import {
  ActiveRowFrame,
  buildRowDefs,
  CompensationRowActive,
  ContractTypeRowActive,
  FixedHoursRowActive,
  formatRow,
  GenericRowActive,
  HolidayRowActive,
  JobProfilesRowActive,
  LimitedTermRowActive,
  LockedRow,
  NoticeRowActive,
  ProbationWeeksRowActive,
  StartDateRowActive,
  canAdvanceRow,
  rowLabel,
  type RowDef,
  type RowId,
} from "@/components/contracts/wizard/DetailsRows";
import { cn } from "@/lib/utils";

// ── Step model ─────────────────────────────────────────────────────────────

type StepKey = "arbeitgeber" | "arbeitnehmer" | "details" | "entwurf" | "signatur" | "fertig";

interface WizardState {
  companyId: string | null;
  branchId: string | null;
  /** EMPTY_GUID = "Ohne Unterzeichner"; any real Guid = picked rep. null = nothing chosen yet. */
  legalRepId: string | null;
  employeeOrdioId: string | null;
  contractType: string | null;
  fieldValues: Record<string, string>;
  /** Id of the contract once it has been created in step 3 → 4 transition. */
  contractId: string | null;
}

const INITIAL_STATE: WizardState = {
  companyId: null,
  branchId: null,
  legalRepId: null,
  employeeOrdioId: null,
  contractType: null,
  fieldValues: {},
  contractId: null,
};

export const Route = createFileRoute("/o/$slug/contracts/new")({
  component: NewContractWizardPage,
});

function NewContractWizardPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const companies = useQuery(companiesQueries.all());
  const configurations = useQuery(contractsQueries.configurations());

  const [step, setStep] = useState<StepKey>("arbeitgeber");
  const [state, setState] = useState<WizardState>(INITIAL_STATE);

  // Auto-pick the (single) company so step 1 doesn't ask the user something
  // they only have one answer to. Multi-company orgs see the picker.
  useEffect(() => {
    if (!state.companyId && companies.data && companies.data.length === 1) {
      setState((s) => ({ ...s, companyId: companies.data![0].id }));
    }
  }, [companies.data, state.companyId]);

  const branches = useQuery({
    ...companiesQueries.branches(state.companyId ?? ""),
    enabled: !!state.companyId,
  });
  const reps = useQuery({
    ...companiesQueries.representatives(state.companyId ?? ""),
    enabled: !!state.companyId,
  });
  const candidates = useQuery({
    ...contractsQueries.employeeCandidates(state.companyId ?? ""),
    enabled: !!state.companyId,
  });

  const selectedCompany = useMemo<CompanyDto | undefined>(
    () => companies.data?.find((c) => c.id === state.companyId),
    [companies.data, state.companyId],
  );
  const selectedBranch = useMemo<BranchDto | undefined>(
    () => branches.data?.find((b) => b.id === state.branchId),
    [branches.data, state.branchId],
  );
  const selectedRep = useMemo<LegalRepresentativeDto | undefined>(
    () =>
      state.legalRepId && state.legalRepId !== EMPTY_GUID
        ? reps.data?.find((r) => r.id === state.legalRepId)
        : undefined,
    [reps.data, state.legalRepId],
  );
  const selectedEmployee = useMemo<OrdioEmployeeDetailDto | undefined>(
    () => candidates.data?.find((c) => c.ordioId === state.employeeOrdioId),
    [candidates.data, state.employeeOrdioId],
  );
  const selectedConfig = useMemo<ContractConfigurationDto | undefined>(
    () => configurations.data?.find((c) => c.contractType === state.contractType),
    [configurations.data, state.contractType],
  );

  // ── Mutations ────────────────────────────────────────────────────────
  const create = useToastMutation({
    mutationFn: () => {
      if (!state.companyId || !state.contractType || !selectedEmployee || !selectedConfig) {
        throw new Error("Wizard state incomplete.");
      }
      const filled: Record<string, string> = { ...state.fieldValues };
      for (const f of selectedConfig.fields) {
        if (filled[f.key] === undefined && f.defaultValue !== null) {
          filled[f.key] = f.defaultValue;
        }
      }
      return contractsApi.create({
        companyId: state.companyId,
        contractType: state.contractType,
        employee: toEmployeeSnapshot(selectedEmployee),
        fieldValues: filled,
        legalRepresentativeId: state.legalRepId ?? EMPTY_GUID,
      });
    },
    toasts: { success: "Entwurf angelegt." },
    onSuccess: (preview) => {
      queryClient.setQueryData(contractsQueries.detail(preview.id).queryKey, preview);
      setState((s) => ({ ...s, contractId: preview.id }));
      setStep("entwurf");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.problem as { errors?: string[] } | null;
        if (body?.errors?.length) {
          // eslint-disable-next-line no-console
          console.error("Contract validation errors:", body.errors);
        }
      }
    },
  });

  const confirm = useToastMutation({
    mutationFn: () => {
      if (!state.contractId) throw new Error("Kein Vertrag zum Bestätigen.");
      return contractsApi.confirm(state.contractId);
    },
    toasts: { success: "Vertrag bestätigt." },
    onSuccess: (preview) => {
      // Keep the detail cache warm and advance to the in-person signing step.
      queryClient.setQueryData(contractsQueries.detail(preview.id).queryKey, preview);
      setStep("signatur");
    },
  });

  const sign = useToastMutation({
    mutationFn: (signatureDataUrl: string) => {
      if (!state.contractId) throw new Error("Kein Vertrag zum Unterzeichnen.");
      return contractsApi.sign(state.contractId, { signatureDataUrl });
    },
    toasts: { success: "Vertrag unterzeichnet." },
    onSuccess: (preview) => {
      queryClient.setQueryData(contractsQueries.detail(preview.id).queryKey, preview);
      setStep("fertig");
    },
  });

  const remove = useToastMutation({
    mutationFn: () => {
      if (!state.contractId) throw new Error("Kein Vertrag zum Löschen.");
      return contractsApi.remove(state.contractId);
    },
    toasts: { success: "Entwurf gelöscht." },
    onSuccess: () => {
      // Drop the wizard state and bounce back to the contracts landing.
      setState(INITIAL_STATE);
      void navigate({ to: "/o/$slug/contracts", params: { slug: org.slug } });
    },
  });

  // ── Per-step progression gates (steps 1 + 2 only) ──────────────────
  // Step 3 (details) drives forward row-by-row inside the step; steps 4–6
  // each own their primary action and don't use the wizard footer.
  const canProceed = (() => {
    switch (step) {
      case "arbeitgeber":
        // legalRepId can be EMPTY_GUID ("Ohne Unterzeichner") — that's a valid choice.
        return !!state.companyId && !!state.branchId && !!state.legalRepId;
      case "arbeitnehmer":
        return !!state.employeeOrdioId;
      default:
        return false;
    }
  })();

  const goNext = () => {
    if (step === "arbeitgeber") setStep("arbeitnehmer");
    else if (step === "arbeitnehmer") setStep("details");
  };
  const goBack = () => {
    if (step === "arbeitnehmer") setStep("arbeitgeber");
    else if (step === "details") setStep("arbeitnehmer");
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <PageContainer className="max-w-6xl">
      <SectionHeader step={step} />

      {step === "arbeitgeber" && (
        <ArbeitgeberStep
          companies={companies}
          state={state}
          selectedCompany={selectedCompany}
          branches={branches}
          reps={reps}
          onPickCompany={(id) =>
            setState((s) => ({
              ...s,
              companyId: id,
              branchId: null,
              legalRepId: null,
              employeeOrdioId: null,
            }))
          }
          onPickBranch={(id) => setState((s) => ({ ...s, branchId: id }))}
          onPickRep={(id) => setState((s) => ({ ...s, legalRepId: id }))}
        />
      )}

      {step === "arbeitnehmer" && (
        <ArbeitnehmerStep
          employer={selectedCompany}
          branch={selectedBranch}
          rep={selectedRep}
          candidates={candidates}
          selectedId={state.employeeOrdioId}
          onPick={(employeeOrdioId) => setState((s) => ({ ...s, employeeOrdioId }))}
        />
      )}

      {step === "details" && (
        <VertragsdetailsStep
          employer={selectedCompany}
          rep={selectedRep}
          employee={selectedEmployee}
          configurations={configurations.data ?? []}
          configurationsLoading={configurations.isLoading}
          state={state}
          onPickType={(contractType) => {
            // Seed every descriptor's defaultValue into fieldValues at
            // pick-time. The row-level WEITER gate reads from fieldValues
            // directly — without seeding, descriptors like
            // notice_period_mode/holiday_mode that ship a server-side
            // default (`Gesetzlich`) would render as "Bitte wählen" and
            // leave WEITER disabled until the operator clicked the chip
            // that already represents the default.
            //
            // probation_weeks is deliberately excluded — the backend's
            // descriptor default of "2" doesn't map to any of the three
            // preset chips (4 / 6 / 8 Wochen) and would force the operator
            // into "Benutzerdefiniert" without an explicit pick.
            const cfg = (configurations.data ?? []).find(
              (c) => c.contractType === contractType,
            );
            const seeded: Record<string, string> = {};
            for (const f of cfg?.fields ?? []) {
              if (f.key === "probation_weeks") continue;
              if (f.defaultValue !== null) seeded[f.key] = f.defaultValue;
            }
            setState((s) => ({ ...s, contractType, fieldValues: seeded }));
          }}
          onChangeValues={(fieldValues) => setState((s) => ({ ...s, fieldValues }))}
          onCreateDraft={() => create.mutate(undefined)}
          creatingDraft={create.isPending}
        />
      )}

      {step === "entwurf" && (
        <EntwurfStep
          contractId={state.contractId}
          onConfirm={() => confirm.mutate(undefined)}
          onDelete={() => remove.mutate(undefined)}
          confirming={confirm.isPending}
          deleting={remove.isPending}
        />
      )}

      {step === "signatur" && (
        <SignaturStep
          contractId={state.contractId}
          onSign={(dataUrl) => sign.mutate(dataUrl)}
          signing={sign.isPending}
        />
      )}

      {step === "fertig" && (
        <FertigStep contractId={state.contractId} orgSlug={org.slug} />
      )}

      {/* Footer nav — only for the steps that don't have inline navigation.
          Step 3 (Vertragsdetails) drives forward row-by-row via WEITER and
          gets its own "Vertrag erstellen" button at the end; steps 4–6 each
          own their primary action. */}
      {(step === "arbeitgeber" || step === "arbeitnehmer") && (
        <footer className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={step === "arbeitgeber"}
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </Button>
          <Button
            onClick={goNext}
            disabled={!canProceed}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Weiter
            <ArrowRight className="h-4 w-4" />
          </Button>
        </footer>
      )}
    </PageContainer>
  );
}

function SectionHeader({ step }: { step: StepKey }) {
  // Data-collection steps share a single "Neuer Arbeitsvertrag" heading so
  // the operator orients on the task, not on a counter. Each downstream
  // step (Entwurf / Unterschrift / Fertig) gets its own title.
  const map: Record<StepKey, { icon: React.ComponentType<{ className?: string }>; title: string }> = {
    arbeitgeber: { icon: FileText, title: "Neuer Arbeitsvertrag" },
    arbeitnehmer: { icon: FileText, title: "Neuer Arbeitsvertrag" },
    details: { icon: FileText, title: "Neuer Arbeitsvertrag" },
    entwurf: { icon: FileText, title: "Entwurf" },
    signatur: { icon: PenLine, title: "Unterschrift" },
    fertig: { icon: CheckCircle2, title: "Fertig" },
  };
  const { icon: Icon, title } = map[step];
  return (
    <header className="mb-4 flex items-center gap-2">
      <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
    </header>
  );
}

// ── Step 1: Arbeitgeber:in ─────────────────────────────────────────────────

function ArbeitgeberStep({
  companies,
  state,
  selectedCompany,
  branches,
  reps,
  onPickCompany,
  onPickBranch,
  onPickRep,
}: {
  companies: ReturnType<typeof useQuery<CompanyDto[], Error>>;
  state: WizardState;
  selectedCompany: CompanyDto | undefined;
  branches: ReturnType<typeof useQuery<BranchDto[], Error>>;
  reps: ReturnType<typeof useQuery<LegalRepresentativeDto[], Error>>;
  onPickCompany: (id: string) => void;
  onPickBranch: (id: string) => void;
  onPickRep: (id: string) => void;
}) {
  const showCompanyPicker = (companies.data?.length ?? 0) > 1;

  return (
    <div className="space-y-6">
      {/* Employer summary card */}
      {selectedCompany ? (
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Arbeitgeber:in
            </div>
            <div className="mt-1 text-lg font-semibold">{selectedCompany.name}</div>
            <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
              {selectedCompany.address && <div>{selectedCompany.address}</div>}
              {(selectedCompany.postcode || selectedCompany.city) && (
                <div>
                  {[selectedCompany.postcode, selectedCompany.city].filter(Boolean).join(" ")}
                </div>
              )}
            </div>
            {showCompanyPicker && (
              <button
                type="button"
                onClick={() => onPickCompany("")}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Anderes Unternehmen wählen
              </button>
            )}
          </CardContent>
        </Card>
      ) : (
        // No company selected yet (multi-company case).
        <section>
          <h2 className="mb-2 text-sm font-medium">Unternehmen wählen</h2>
          {companies.isLoading ? (
            <CardSkeletonRow count={2} />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {companies.data?.map((c) => (
                <PickCard
                  key={c.id}
                  title={c.name}
                  subtitle={[c.address, c.city].filter(Boolean).join(", ")}
                  active={c.id === state.companyId}
                  onClick={() => onPickCompany(c.id)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {selectedCompany && (
        <>
          {/* Filiale picker */}
          <section>
            <h2 className="mb-2 text-sm font-medium">Filiale wählen</h2>
            {branches.isLoading ? (
              <CardSkeletonRow count={2} />
            ) : branches.data && branches.data.length > 0 ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {branches.data.map((b) => (
                  <PickCard
                    key={b.id}
                    title={b.name}
                    subtitle={[b.address, b.city].filter(Boolean).join(", ")}
                    active={b.id === state.branchId}
                    onClick={() => onPickBranch(b.id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Filialen verfügbar.</p>
            )}
          </section>

          {/* Vertragsunterzeichner:in picker */}
          <section>
            <h2 className="mb-2 text-sm font-medium">Vertragsunterzeichner:in wählen</h2>
            {reps.isLoading ? (
              <CardSkeletonRow count={2} />
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {(reps.data ?? []).map((r) => (
                  <PickCard
                    key={r.id}
                    title={r.name}
                    subtitle={r.hasSignature ? "Digitale Unterschrift" : "Ohne Unterschrift"}
                    subtitleClass={r.hasSignature ? "text-emerald-600" : undefined}
                    active={r.id === state.legalRepId}
                    onClick={() => onPickRep(r.id)}
                  />
                ))}
                <PickCard
                  title="Ohne Unterzeichner"
                  subtitle="Vertrag ohne Vertreter:in anlegen"
                  active={state.legalRepId === EMPTY_GUID}
                  onClick={() => onPickRep(EMPTY_GUID)}
                />
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ── Step 2: Arbeitnehmer:in ────────────────────────────────────────────────

function ArbeitnehmerStep({
  employer,
  branch,
  rep,
  candidates,
  selectedId,
  onPick,
}: {
  employer: CompanyDto | undefined;
  branch: BranchDto | undefined;
  rep: LegalRepresentativeDto | undefined;
  candidates: ReturnType<typeof useQuery<OrdioEmployeeDetailDto[], Error>>;
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2">
        <SummaryCard
          accent="primary"
          icon={<Building2 className="h-4 w-4 text-primary" />}
          label="Arbeitgeber:in"
          title={employer?.name ?? "—"}
          lines={[
            branch?.name,
            rep?.name ? `Vertreten durch ${rep.name}` : "Ohne Unterzeichner",
          ].filter((l): l is string => !!l)}
        />
      </div>

      <section>
        <h2 className="mb-2 text-sm font-medium">Mitarbeiter:in auswählen</h2>
        {candidates.isLoading ? (
          <CardSkeletonRow count={4} />
        ) : candidates.error ? (
          <p className="text-sm text-destructive">
            Konnte Mitarbeitendenliste nicht laden: {(candidates.error as Error).message}
          </p>
        ) : (candidates.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            Keine Mitarbeitendenkandidaten in Ordio — die Person muss erst dort angelegt werden.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {candidates.data!.map((c) => {
              const sub = [c.email, c.phone].filter(Boolean).join(" · ");
              return (
                <PickCard
                  key={c.ordioId}
                  title={employeeDisplayName(c)}
                  subtitle={sub || c.ordioId}
                  icon={<User2 className="h-4 w-4 text-muted-foreground" />}
                  active={c.ordioId === selectedId}
                  onClick={() => onPick(c.ordioId)}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Step 3: Vertragsdetails — row-by-row sequential confirm-lock ───────────
//
// Mirrors `ContractWizard.razor` step 2: rows below the frontier are LOCKED
// (label + value + pencil-to-edit), the active row gets a primary-accented
// paper wrapper with bespoke controls per setting, and the global "Vertrag
// erstellen" button only appears once every row has been confirmed. See
// `components/contracts/wizard/DetailsRows.tsx` for the row controls
// themselves (Euro/Cent stepper, Befristung/Probezeit/Kündigungsfrist chip
// groups, brutto-chain readout, …).

function VertragsdetailsStep({
  employer,
  rep,
  employee,
  configurations,
  configurationsLoading,
  state,
  onPickType,
  onChangeValues,
  onCreateDraft,
  creatingDraft,
}: {
  employer: CompanyDto | undefined;
  rep: LegalRepresentativeDto | undefined;
  employee: OrdioEmployeeDetailDto | undefined;
  configurations: ContractConfigurationDto[];
  configurationsLoading: boolean;
  state: WizardState;
  onPickType: (type: string) => void;
  onChangeValues: (next: Record<string, string>) => void;
  onCreateDraft: () => void;
  creatingDraft: boolean;
}) {
  const selectedConfig = configurations.find((c) => c.contractType === state.contractType);
  const rowDefs = useMemo<RowDef[]>(
    () => (selectedConfig ? buildRowDefs(selectedConfig) : []),
    [selectedConfig],
  );

  // Row state machine — `activeRow` is the row the user is editing,
  // `maxReached` is the highest row ever confirmed. The two coincide while
  // the user moves forward; clicking a pencil drops `activeRow` below
  // `maxReached` for an in-place edit, and clicking WEITER snaps back to the
  // frontier so they resume where they left off.
  const [activeRow, setActiveRow] = useState(0);
  const [maxReached, setMaxReached] = useState(0);

  // contract_type is rendered as the first row. Total = rowDefs + 1 (the type
  // row itself). lastIdx is the very last data row; once activeRow passes it
  // the operator clicks "Vertrag erstellen" instead of WEITER.
  const totalRows = 1 + rowDefs.length;
  const lastIdx = totalRows - 1;

  // Setter that funnels each row's edits into the wizard's fieldValues. The
  // wizard owns the dictionary so it survives navigation away and back.
  // We expose both a single-key form and a batch (patch) form because some
  // rows have to update two keys at once (Kündigungsfrist mode+months,
  // Urlaub mode+days). Issuing two sequential setValue calls would race —
  // both reads see the same stale snapshot, the second write wins.
  const setValues = (patch: Record<string, string>) => {
    const next = { ...state.fieldValues };
    for (const [k, v] of Object.entries(patch)) {
      if (v === "") delete next[k];
      else next[k] = v;
    }
    onChangeValues(next);
  };
  const setValue = (key: string, value: string) => setValues({ [key]: value });

  // Resolve the row at `idx`. Index 0 is contract_type; index ≥ 1 maps to
  // rowDefs[idx-1].
  const rowIdAt = (idx: number): RowId | null => {
    if (idx === 0) return "contract_type";
    const def = rowDefs[idx - 1];
    return def?.id ?? null;
  };

  const canAdvance = (idx: number): boolean => {
    if (!selectedConfig) return false;
    const id = rowIdAt(idx);
    if (id === "contract_type") return !!state.contractType;
    if (!id) return false;
    return canAdvanceRow(id, state.fieldValues, selectedConfig);
  };

  const advance = () => {
    if (!canAdvance(activeRow)) return;
    if (activeRow < maxReached) {
      // Editing an earlier row — snap back to the frontier.
      setActiveRow(maxReached);
      return;
    }
    const next = activeRow + 1;
    setActiveRow(next);
    setMaxReached((m) => Math.max(m, next));
  };

  const allDone = maxReached > lastIdx && activeRow > lastIdx;
  const renderUpTo = Math.min(maxReached, lastIdx);
  const activeIsFrontier = activeRow >= maxReached;

  // Row-entry seeding. Mirrors ContractWizard.razor#SeedDefaultsForRow.
  // The descriptor-level `defaultValue` is already applied via onPickType,
  // but some rows have controls whose initial display reads `Min` (or a
  // mode-default) without persisting it to fieldValues — the operator sees
  // "13,90 €/Std" on the Stundenlohn stepper and a green-styled WEITER, then
  // can't advance because the underlying state is still "". Seeding the
  // first-touch value here lets WEITER reflect what the operator already
  // sees on screen.
  useEffect(() => {
    if (!selectedConfig) return;
    const id = rowIdAt(activeRow);
    if (!id) return;
    const v = state.fieldValues;
    const f = (k: string) => selectedConfig.fields.find((x) => x.key === k);
    const patch: Record<string, string> = {};
    if (id === "start_date") {
      // Server's start_date descriptor doesn't ship a defaultValue (the
      // "natural" default — today — depends on the time the wizard is
      // opened, not on a static field config). Seed it here so WEITER on
      // the Arbeitsbeginn row reflects the value the operator can see in
      // the date picker.
      const fd = f("start_date");
      if (!v.start_date) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        patch.start_date = fd?.defaultValue ?? `${yyyy}-${mm}-${dd}`;
      }
    } else if (id === "compensation") {
      const fc = f("compensation_value");
      if (fc?.min != null && !v.compensation_value) {
        patch.compensation_value = fc.min.toFixed(2);
      }
    } else if (id === "fixed_hours") {
      const fdMode = f("fixed_hours_mode");
      const fdHours = f("fixed_hours");
      if (fdMode == null && fdHours && !v.fixed_hours) {
        // Vollzeit/Teilzeit shape — pure decimal input. Teilzeit's
        // conventional default is 20 h/Woche (half of full-time);
        // Vollzeit defaults to its descriptor `min` (40). The choice is
        // based on contractType rather than a `min == 1` heuristic so it
        // stays robust if the backend ever widens the Teilzeit range.
        const conventional = state.contractType === "Teilzeit" ? 20 : fdHours.min;
        if (conventional != null) patch.fixed_hours = String(conventional);
      }
    }
    if (Object.keys(patch).length > 0) setValues(patch);
    // setValues intentionally not in deps — it closes over state which would
    // re-fire the effect on every fieldValues change. activeRow + config is
    // the trigger; the effect is a no-op when the key already has a value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRow, selectedConfig]);
  const hasActive = activeRow < totalRows;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2">
        <SummaryCard
          accent="primary"
          icon={<Building2 className="h-4 w-4 text-primary" />}
          label="Arbeitgeber:in"
          title={employer?.name ?? "—"}
          lines={[
            employer?.address ?? undefined,
            [employer?.postcode, employer?.city].filter(Boolean).join(" ") || undefined,
            rep?.name ? `Vertreten durch ${rep.name}` : "Ohne Unterzeichner",
          ].filter((l): l is string => !!l)}
        />
        <SummaryCard
          accent="emerald"
          icon={<User2 className="h-4 w-4 text-emerald-600" />}
          label="Arbeitnehmer:in"
          title={employee ? employeeDisplayName(employee) : "—"}
          lines={[
            employee?.email ?? undefined,
            employee?.phone ?? undefined,
            [employee?.address, employee?.postcode, employee?.city]
              .filter(Boolean)
              .join(", ") || undefined,
          ].filter((l): l is string => !!l)}
        />
      </div>

      {configurationsLoading && !selectedConfig && (
        <div className="h-0.5 w-full animate-pulse rounded bg-primary/30" />
      )}

      {/* Row layout depends on where the active row sits in the sequence:
            • Frontier (the operator is on the latest row they've ever
              reached) → the active row gets its own primary-accented frame
              below the "Bisherige Angaben" panel, so chip groups and
              steppers have full width.
            • Editing an earlier row → the table is **split** in place. The
              locked rows above the edit row stay above, the active control
              expands inline at the edit-row's index, and the locked rows
              after it stay below. Mirrors ContractWizard.razor's behaviour:
              clicking a pencil keeps the row in place rather than yanking
              the focus to the bottom. */}
      {(() => {
        const renderActive = () =>
          activeRow === 0 ? (
            <ContractTypeRowActive
              configurations={configurations}
              selectedType={state.contractType}
              onPick={(t) => {
                if (t !== state.contractType) setMaxReached(0);
                onPickType(t);
              }}
              onAdvance={advance}
            />
          ) : (
            selectedConfig && (
              <ActiveRow
                rowId={rowIdAt(activeRow)!}
                fieldValues={state.fieldValues}
                setValue={setValue}
                setValues={setValues}
                config={selectedConfig}
                configurations={configurations}
                onAdvance={advance}
                canAdvance={canAdvance(activeRow)}
                onPickType={onPickType}
                contractType={state.contractType}
              />
            )
          );

        const renderLocked = (i: number) => {
          if (!selectedConfig && i > 0) return null;
          const id = rowIdAt(i);
          if (!id) return null;
          // contract_type's "config" comes from the picked contract type
          // itself — even pre-pick we want a placeholder label.
          const cfgForLabel = selectedConfig ?? configurations[0];
          if (!cfgForLabel) return null;
          return (
            <LockedRow
              key={`locked-${i}`}
              label={rowLabel(id, cfgForLabel)}
              value={formatRow(id, state.fieldValues, cfgForLabel)}
              onEdit={() => {
                if (i <= maxReached) setActiveRow(i);
              }}
            />
          );
        };

        // All rows confirmed — the "Vertrag erstellen" button is about to
        // appear below. Keep the locked rows on screen so the operator can
        // review (and pencil-edit) every value right up to the moment they
        // commit. Without this branch the table would vanish.
        if (!hasActive) {
          const all: React.ReactNode[] = [];
          for (let i = 0; i <= lastIdx; i++) all.push(renderLocked(i));
          const filtered = all.filter(Boolean);
          if (filtered.length === 0) return null;
          return (
            <Card>
              <CardContent className="p-4">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <ListChecks className="h-3.5 w-3.5 text-primary" />
                  Bisherige Angaben
                </div>
                <div className="border-t">{filtered}</div>
              </CardContent>
            </Card>
          );
        }

        if (activeIsFrontier) {
          // Locked rows live in a single panel above; active row in its
          // own frame below.
          const above = [];
          for (let i = 0; i < activeRow; i++) above.push(renderLocked(i));
          const filtered = above.filter(Boolean);
          return (
            <>
              {filtered.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <ListChecks className="h-3.5 w-3.5 text-primary" />
                      Bisherige Angaben
                    </div>
                    <div className="border-t">{filtered}</div>
                  </CardContent>
                </Card>
              )}
              <ActiveRowFrame>{renderActive()}</ActiveRowFrame>
            </>
          );
        }

        // Editing-earlier-row branch — split the table inline.
        const rows: React.ReactNode[] = [];
        for (let i = 0; i <= renderUpTo; i++) {
          if (i === activeRow) {
            rows.push(
              <div key={`active-${i}`} className="py-2">
                <ActiveRowFrame>{renderActive()}</ActiveRowFrame>
              </div>,
            );
          } else {
            rows.push(renderLocked(i));
          }
        }
        return (
          <Card>
            <CardContent className="p-4">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5 text-primary" />
                Bisherige Angaben
              </div>
              <div className="border-t">{rows}</div>
            </CardContent>
          </Card>
        );
      })()}

      {allDone && (
        <footer className="flex justify-end pt-2">
          <Button
            onClick={onCreateDraft}
            disabled={creatingDraft}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {creatingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Vertrag erstellen
          </Button>
        </footer>
      )}
    </div>
  );
}

/**
 * Single dispatcher for active-row rendering. Routes by RowId to the bespoke
 * control, falling back to a generic Input for unknown keys.
 */
function ActiveRow({
  rowId,
  fieldValues,
  setValue,
  setValues,
  config,
  configurations,
  onAdvance,
  canAdvance,
  onPickType,
  contractType,
}: {
  rowId: RowId;
  fieldValues: Record<string, string>;
  setValue: (key: string, value: string) => void;
  setValues: (patch: Record<string, string>) => void;
  config: ContractConfigurationDto;
  configurations: ContractConfigurationDto[];
  onAdvance: () => void;
  canAdvance: boolean;
  onPickType: (type: string) => void;
  contractType: string | null;
}) {
  const baseProps = { fieldValues, setValue, config, onAdvance, canAdvance };
  switch (rowId) {
    case "contract_type":
      return (
        <ContractTypeRowActive
          configurations={configurations}
          selectedType={contractType}
          onPick={onPickType}
          onAdvance={onAdvance}
        />
      );
    case "start_date":
      return <StartDateRowActive {...baseProps} />;
    case "limited_term":
      return <LimitedTermRowActive {...baseProps} />;
    case "job_profiles":
      return <JobProfilesRowActive {...baseProps} />;
    case "compensation":
      return <CompensationRowActive {...baseProps} />;
    case "fixed_hours":
      return <FixedHoursRowActive {...baseProps} setValues={setValues} />;
    case "probation_weeks":
      return <ProbationWeeksRowActive {...baseProps} />;
    case "notice":
      return <NoticeRowActive {...baseProps} setValues={setValues} />;
    case "holiday":
      return <HolidayRowActive {...baseProps} setValues={setValues} />;
    default:
      return <GenericRowActive {...baseProps} fieldKey={rowId} />;
  }
}

// ── Step 4: Entwurf ────────────────────────────────────────────────────────

function EntwurfStep({
  contractId,
  onConfirm,
  onDelete,
  confirming,
  deleting,
}: {
  contractId: string | null;
  onConfirm: () => void;
  onDelete: () => void;
  confirming: boolean;
  deleting: boolean;
}) {
  const preview = useQuery({
    ...contractsQueries.detail(contractId ?? ""),
    enabled: !!contractId,
  });

  if (!contractId) {
    return (
      <p className="text-sm text-muted-foreground">
        Kein Entwurf zum Anzeigen — bitte zurück zu „Vertragsdetails".
      </p>
    );
  }
  if (preview.isLoading) return <Skeleton className="h-64" />;
  if (preview.error)
    return (
      <p className="text-sm text-destructive">
        Konnte Entwurf nicht laden: {(preview.error as Error).message}
      </p>
    );
  if (!preview.data) return null;
  const p = preview.data;
  const employer = p.employer;
  const employee = p.employee;
  const busy = confirming || deleting;

  return (
    <div className="space-y-4">
      {/* Header — identifier + status pill only. Action buttons live at the
          bottom of the page, below the PDF preview, so the user reads the
          contract before committing. */}
      <header className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-muted-foreground" />
        <span className="font-mono text-sm">Vertrag #{p.id.slice(0, 8)}…</span>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
          Draft
        </span>
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <SummaryCard
          accent="primary"
          icon={<Building2 className="h-4 w-4 text-primary" />}
          label="Arbeitgeber:in"
          title={employer.name}
          lines={[
            employer.address ?? undefined,
            [employer.postcode, employer.city].filter(Boolean).join(" ") || undefined,
            employer.legalRepresentativeName
              ? `Vertreten durch ${employer.legalRepresentativeName}`
              : "Ohne Unterzeichner",
          ].filter((l): l is string => !!l)}
        />
        <SummaryCard
          accent="emerald"
          icon={<User2 className="h-4 w-4 text-emerald-600" />}
          label="Arbeitnehmer:in"
          title={[employee.salutation, employee.firstName, employee.lastName]
            .filter(Boolean)
            .join(" ")}
          lines={[
            employee.email ?? undefined,
            employee.phone ?? undefined,
            [employee.addressStreet, employee.addressZip, employee.addressCity]
              .filter(Boolean)
              .join(", ") || undefined,
          ].filter((l): l is string => !!l)}
        />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-sm">
            <Euro className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Anstellungsart</span>
          </div>
          <div className="mt-1 text-2xl font-semibold">{p.contractType}</div>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            Lohn wird in Ordio angelegt
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="border-b px-5 py-3 text-sm font-medium">Vertragsdetails</div>
          <dl className="divide-y">
            {Object.entries(p.fieldValues).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[12rem_1fr] gap-3 px-5 py-2.5 text-sm">
                <dt className="uppercase tracking-wide text-muted-foreground text-xs">
                  {k}:
                </dt>
                <dd>{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* PDF preview — bearer-protected endpoint, loaded as a Blob → object URL */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b px-5 py-3 text-sm font-medium">
            <span>Vorschau</span>
            <a
              href={contractsApi.pdfUrl(contractId, "draft")}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              PDF in neuem Tab öffnen
            </a>
          </div>
          <PdfPreview contractId={contractId} variant="draft" />
        </CardContent>
      </Card>

      {/* Action bar — destructive on the left, primary on the right. */}
      <footer className="flex items-center justify-between gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (window.confirm("Entwurf wirklich löschen?")) onDelete();
          }}
          disabled={busy}
          className="text-destructive"
        >
          {deleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Entwurf löschen
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Vertrag bestätigen
        </Button>
      </footer>
    </div>
  );
}

function PdfPreview({
  contractId,
  variant,
}: {
  contractId: string;
  variant: "draft" | "confirmed" | "signed";
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setBlobUrl(null);
    setError(null);
    (async () => {
      try {
        const blob = await contractsApi.fetchPdfBlob(contractId, variant);
        if (cancelled) return;
        // Re-wrap with an explicit application/pdf type. Firefox's inline PDF
        // viewer only kicks in when the blob's MIME is exactly that, and a
        // missing/wrong response Content-Type would otherwise trigger a
        // download instead of a preview.
        const pdfBlob = blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
        url = URL.createObjectURL(pdfBlob);
        setBlobUrl(url);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [contractId, variant]);

  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        Konnte Vorschau nicht laden: {error}
      </div>
    );
  }
  if (!blobUrl) return <Skeleton className="m-5 h-[60vh]" />;
  return (
    <object
      data={blobUrl}
      type="application/pdf"
      title="Vertragsentwurf"
      className="block h-[70vh] w-full bg-muted/30"
    >
      <iframe
        src={blobUrl}
        title="Vertragsentwurf"
        className="block h-[70vh] w-full bg-muted/30"
      />
    </object>
  );
}

// ── Step 5: Signatur ───────────────────────────────────────────────────────

function SignaturStep({
  contractId,
  onSign,
  signing,
}: {
  contractId: string | null;
  onSign: (dataUrl: string) => void;
  signing: boolean;
}) {
  const preview = useQuery({
    ...contractsQueries.detail(contractId ?? ""),
    enabled: !!contractId,
  });
  const padRef = useRef<SignaturePadHandle | null>(null);
  const [empty, setEmpty] = useState(true);

  if (!contractId) return null;
  if (preview.isLoading) return <Skeleton className="h-64" />;
  if (preview.error)
    return (
      <p className="text-sm text-destructive">
        Konnte Vertrag nicht laden: {(preview.error as Error).message}
      </p>
    );
  if (!preview.data) return null;
  const p = preview.data;

  const handleSign = () => {
    const dataUrl = padRef.current?.getDataUrl();
    if (!dataUrl) return;
    onSign(dataUrl);
  };
  const handleClear = () => padRef.current?.clear();

  return (
    <div className="space-y-4">
      {/* Hand-off hint — operator passes the device to the employee. */}
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
        Bitte gib das Gerät an{" "}
        <strong>{p.employeeDisplayName}</strong> weiter.
      </div>

      {/* Compact summary card — minimum info to confirm we're signing the right contract. */}
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-4">
          <SummaryItem label="Unternehmen" value={p.companyName} />
          <SummaryItem label="Mitarbeiter:in" value={p.employeeDisplayName} />
          {p.fieldValues.start_date && (
            <SummaryItem label="Startdatum" value={p.fieldValues.start_date} />
          )}
          {p.fieldValues.compensation_value && (
            <SummaryItem
              label="Stundenlohn"
              value={`${p.fieldValues.compensation_value} €/Std.`}
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmed PDF preview — what the employee is signing. */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b px-5 py-3 text-sm font-medium">
            <span>Vertragsvorschau</span>
            <a
              href={contractsApi.pdfUrl(contractId, "confirmed")}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              PDF in neuem Tab öffnen
            </a>
          </div>
          <PdfPreview contractId={contractId} variant="confirmed" />
        </CardContent>
      </Card>

      {/* Signature pad */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Unterschrift der Arbeitnehmer:in
          </div>
          <SignaturePad ref={padRef} onEmptyChange={setEmpty} />
        </CardContent>
      </Card>

      <footer className="flex items-center justify-between gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleClear}
          disabled={signing}
        >
          <Eraser className="h-4 w-4" />
          Unterschrift löschen
        </Button>
        <Button
          onClick={handleSign}
          disabled={empty || signing}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
          Unterzeichnen
        </Button>
      </footer>
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

// ── Step 6: Fertig ─────────────────────────────────────────────────────────

function FertigStep({
  contractId,
  orgSlug,
}: {
  contractId: string | null;
  orgSlug: string;
}) {
  if (!contractId) return null;
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100">
        <div className="flex items-center gap-2 font-medium">
          <CheckCircle2 className="h-4 w-4" />
          Vertrag wurde erfolgreich unterzeichnet.
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b px-5 py-3 text-sm font-medium">
            <span>Finaler Vertrag mit Unterschriften</span>
            <a
              href={contractsApi.pdfUrl(contractId, "signed")}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              PDF in neuem Tab öffnen
            </a>
          </div>
          <PdfPreview contractId={contractId} variant="signed" />
        </CardContent>
      </Card>

      <footer className="flex items-center justify-end gap-3 pt-2">
        <Button variant="outline" asChild>
          <Link to="/o/$slug/contracts" params={{ slug: orgSlug }}>
            <ListChecks className="h-4 w-4" />
            Zur Vertragsübersicht
          </Link>
        </Button>
        <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Link
            to="/o/$slug/contracts/$contractId"
            params={{ slug: orgSlug, contractId }}
          >
            <FileText className="h-4 w-4" />
            Vertrag öffnen
          </Link>
        </Button>
      </footer>
    </div>
  );
}

// ── Small shared bits ──────────────────────────────────────────────────────

function PickCard({
  title,
  subtitle,
  subtitleClass,
  icon,
  active,
  onClick,
}: {
  title: string;
  subtitle?: string;
  subtitleClass?: string;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-2 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50",
        active && "border-primary ring-1 ring-primary/30",
      )}
    >
      {icon}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{title}</div>
        {subtitle && (
          <div className={cn("truncate text-xs text-muted-foreground", subtitleClass)}>
            {subtitle}
          </div>
        )}
      </div>
      {active && <Check className="h-4 w-4 text-primary" />}
    </button>
  );
}

function SummaryCard({
  accent,
  icon,
  label,
  title,
  lines,
}: {
  accent: "primary" | "emerald";
  icon: React.ReactNode;
  label: string;
  title: string;
  lines: string[];
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 border-l-4",
        accent === "primary" ? "border-l-primary" : "border-l-emerald-500",
      )}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-base font-semibold">{title}</div>
      <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div>
    </div>
  );
}

function CardSkeletonRow({ count }: { count: number }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-14" />
      ))}
    </div>
  );
}

